/**
 * Every Odoo environment the signed-in user may see, in ONE ArgoCD request.
 *
 * The environments card and the branch page ask ArgoCD for one project's
 * Applications (`waadoo.ovh/project=<id>`). The home page and the sidebar
 * need all of them at once; asking per project would cost one request per
 * project. One list call with a label selector answers for every project,
 * and ArgoCD still filters it by the caller's RBAC, since the request
 * carries their own Authelia id_token (see `argoApiFetch`).
 *
 * UN SEUL SÉLECTEUR depuis l'étape 13 (2026-10-22) : le label de projet
 * `waadoo.ovh/project` du contrat d'identité (platform-catalog
 * `contracts/identity.md`), que portent toutes les Applications
 * d'environnement depuis la migration de l'étape 11. L'ancien `odoo-project`
 * n'est plus demandé.
 *
 * THE SELECTOR IS THE LABEL'S EXISTENCE, NOT `app-type`: that label varies
 * with the type (`<type>-appenv` in the appenv skeleton of platform-catalog,
 * `_common/skeleton-appenv/base/argocd-application.yaml`), while every
 * environment Application, permanent or ephemeral, carries
 * `waadoo.ovh/project` — so selecting on its existence returns exactly the
 * union of the per-project answers. The ephemeral wrapper
 * Applications (`appenv-prj-<p>-<branche>`, `app-type: appenv`) do not carry
 * it (contracts/identity.md) and stay out, as they must, or they would be
 * listed as environments.
 */

import {
  fetchArgoJson,
  type ArgoApplication,
  type ArgoJsonRequestOptions,
} from './argoApp';
import type { FetchApi } from '@backstage/frontend-plugin-api';
import {
  createSharedLoad,
  type SharedLoad,
  type SharedLoadOptions,
} from './sharedLoad';
import { APP_PROJECT_LABEL, applicationProjectOf } from '../shared';

/**
 * How long one answer is reused. Short: it only has to cover the sidebar and
 * the home page asking at the same time, and a quick back-and-forth between
 * pages — not to stand in for the Refresh button.
 */
const APPLICATIONS_TTL_MS = 30_000;

const sharedApplications =
  createSharedLoad<ArgoApplication[]>(APPLICATIONS_TTL_MS);

/** Never pruned: one entry per project opened in the session, gone on reload. */
const projectLoads = new WeakMap<
  FetchApi,
  Map<string, SharedLoad<ArgoApplication[]>>
>();

/**
 * Listeners of {@link onProjectApplications}, per FetchApi. Never pruned
 * beyond what unsubscribing removes.
 */
const projectListeners = new WeakMap<
  FetchApi,
  Set<(project: string, apps: ArgoApplication[]) => void>
>();

/**
 * Calls `listener` with every list of one project's Applications ArgoCD
 * answers through this FetchApi from now on — not with the shared answers
 * handed out again. The « Pipelines » tile of the « Projet » card
 * (useRunningPipelines.ts) learns this way the branches the environments
 * card's own reads find (Refresh, the « being deleted » poll), without a
 * request of its own. Returns the unsubscribe.
 */
export function onProjectApplications(
  fetchApi: FetchApi,
  listener: (project: string, apps: ArgoApplication[]) => void,
): () => void {
  let listeners = projectListeners.get(fetchApi);
  if (!listeners) {
    listeners = new Set();
    projectListeners.set(fetchApi, listeners);
  }
  const subscribed = listeners;
  subscribed.add(listener);
  return () => {
    subscribed.delete(listener);
  };
}

/** The request options minus the path, which this module owns. */
export type ApplicationsRequest = Omit<ArgoJsonRequestOptions, 'path'>;

/** The ArgoCD path of one list call, selecting on `selector`. */
function applicationsPath(selector: string): string {
  return `applications?selector=${encodeURIComponent(selector)}`;
}

/** The ArgoCD path of the list of every environment. */
export function allApplicationsPath(): string {
  return applicationsPath(APP_PROJECT_LABEL);
}

/** The ArgoCD path of one project's Applications. */
export function projectApplicationsPath(project: string): string {
  return applicationsPath(`${APP_PROJECT_LABEL}=${project}`);
}

/** The Applications of the list call `path`. Rejects when ArgoCD does. */
async function fetchApplications(
  request: ApplicationsRequest,
  path: string,
): Promise<ArgoApplication[]> {
  const list = await fetchArgoJson<{ items?: ArgoApplication[] | null }>({
    ...request,
    path,
  });
  // ArgoCD returns items: null, not [], when nothing matches.
  return list.items ?? [];
}

/**
 * All environment Applications visible to the caller. Rejects when ArgoCD
 * does; the rejection is not cached.
 */
export function loadAllApplications(
  request: ApplicationsRequest,
  options?: SharedLoadOptions,
): Promise<ArgoApplication[]> {
  return sharedApplications.load(
    () => fetchApplications(request, allApplicationsPath()),
    options,
  );
}

/**
 * ONE PROJECT'S Applications (`waadoo.ovh/project=<project>`, those whose
 * `applicationProjectOf` is the project), as the environments card and the
 * branch page read them — shared between them for
 * {@link APPLICATIONS_TTL_MS}, so moving from the project page to a branch
 * page, or from branch to branch, does not ask ArgoCD again. Per `FetchApi`
 * instance, as in `grafanaOrgs.ts`, and module state: the answer is what
 * ArgoCD lets the signed-in caller see (their id_token), and signing out
 * reloads the page (`AppIdentityProxy.signOut`), which drops it. The
 * Refresh buttons and the « being deleted » polls pass `force`. Rejects when
 * ArgoCD does; the rejection is not cached.
 */
export function loadProjectApplications(
  request: ApplicationsRequest & { project: string },
  options?: SharedLoadOptions,
): Promise<ArgoApplication[]> {
  const { project, ...argo } = request;
  let byProject = projectLoads.get(argo.fetchApi);
  if (!byProject) {
    byProject = new Map();
    projectLoads.set(argo.fetchApi, byProject);
  }
  let shared = byProject.get(project);
  if (!shared) {
    shared = createSharedLoad<ArgoApplication[]>(APPLICATIONS_TTL_MS);
    byProject.set(project, shared);
  }
  return shared.load(async () => {
    const items = (
      await fetchApplications(argo, projectApplicationsPath(project))
    ).filter(app => applicationProjectOf(app) === project);
    for (const listener of [...(projectListeners.get(argo.fetchApi) ?? [])]) {
      // A listener that throws must not fail the read shared by every caller.
      try {
        listener(project, items);
      } catch {
        // Its own business; the others are still told.
      }
    }
    return items;
  }, options);
}

/**
 * Calls `listener` with every list of all the environments ArgoCD answers
 * from now on (`SharedLoad.subscribe`) — the sidebar's, the home page's, a
 * « being deleted » poll's, the re-read on returning to the tab. Returns the
 * unsubscribe.
 */
export function onAllApplications(
  listener: (apps: ArgoApplication[]) => void,
): () => void {
  return sharedApplications.subscribe(listener);
}

/**
 * The next {@link loadAllApplications} asks ArgoCD again — after a branch
 * creation or deletion (`branchReads.ts`).
 */
export function forgetAllApplications(): void {
  sharedApplications.forget();
}

/** The next {@link loadProjectApplications} of `project` asks ArgoCD again. */
export function forgetProjectApplications(
  fetchApi: FetchApi,
  project: string,
): void {
  projectLoads.get(fetchApi)?.delete(project);
}

/**
 * The Applications of the list, by their project (`applicationProjectOf`: the
 * `waadoo.ovh/project` label). An Application whose label is empty belongs
 * to no project and is dropped.
 */
export function applicationsByProject(
  apps: ArgoApplication[],
): Map<string, ArgoApplication[]> {
  const byProject = new Map<string, ArgoApplication[]>();
  for (const app of apps) {
    const projectId = applicationProjectOf(app);
    if (!projectId) {
      continue;
    }
    const list = byProject.get(projectId);
    if (list) {
      list.push(app);
    } else {
      byProject.set(projectId, [app]);
    }
  }
  return byProject;
}
